import { useIdentityStore } from "@/data/stores/identity";
import { useOperatorSettingsStore } from "@/data/stores/operatorSettings";
import { useTableAllocationStore } from "@/data/stores/tableAllocation";

import { Language } from "@/data/model/Enums";
import type { IMultiLanguageText } from "@/data/model/Shared";
import { Format } from "@/data/util/Format";
import { ref } from "vue";

// a ref, so a template that translates content - through clientLanguage() here, or through
// translate() and translateByLocationLanguage() below - re-renders when the language changes.
// the ui labels in trans / translations carry their own reactivity
const clientLang = ref<Language | undefined>(undefined);

export function translate(textToTranslate?: IMultiLanguageText): string {
    if (textToTranslate == undefined) return "";

    return Format.translate(textToTranslate, clientLanguage());
}

// the language the waiters of the location share: the operator settings carry it in the Operator App,
// the Mobile POS gets it with the table allocation. end users keep the language they chose
function locationLanguage(): Language | undefined {
    const operatorSettingsLanguage = useOperatorSettingsStore().locationLanguage;
    if (operatorSettingsLanguage != null) return operatorSettingsLanguage;

    return useIdentityStore().hasOperatorAccess ? useTableAllocationStore().locationLanguage : undefined;
}

// a language that was left empty counts as not filled, so the name falls back to the first filled one
// of EN > DE > FR > IT
function withoutBlankTexts(text: IMultiLanguageText): IMultiLanguageText {
    const filled = (value?: string) => (value == null || value.trim() === "" ? undefined : value);

    return { de: filled(text.de), en: filled(text.en), fr: filled(text.fr), it: filled(text.it) };
}

export function translateByLocationLanguage(textToTranslate?: IMultiLanguageText): string {
    if (textToTranslate == undefined) return "";

    const language = locationLanguage();
    // end users translate exactly like translate() does
    if (language == null) return Format.translate(textToTranslate, clientLanguage());

    return Format.translate(withoutBlankTexts(textToTranslate), language);
}

export function clientLocale(): string {
    switch (clientLanguage()) {
        case Language.German:
            return "de";
        case Language.French:
            return "fr";
        case Language.English:
            return "en-GB";
        case Language.Italian:
            return "it";
    }
}

export function getLanguageString(lang: Language) {
    switch (lang) {
        case Language.German:
            return "de";
        case Language.English:
            return "en";
        case Language.French:
            return "fr";
        case Language.Italian:
            return "it";
    }
}

export function clientLanguage(): Language {
    if (clientLang.value === undefined) {
        const language = window.navigator["userLanguage"] || window.navigator.language;
        if (language === "en" || language.includes("en")) clientLang.value = Language.English;
        else if (language === "de" || language.includes("de")) clientLang.value = Language.German;
        else if (language === "fr" || language.includes("fr")) clientLang.value = Language.French;
        else if (language === "it" || language.includes("it")) clientLang.value = Language.Italian;
        else clientLang.value = Language.English;
    }
    return clientLang.value;
}

export function changeLanguage(language: Language | undefined): void {
    // override client language
    clientLang.value = language;
}

export const languageSelectList = [
    {
        id: Language.English,
        name: Language[Language.English],
    },
    {
        id: Language.German,
        name: Language[Language.German],
    },
    {
        id: Language.French,
        name: Language[Language.French],
    },
    {
        id: Language.Italian,
        name: Language[Language.Italian],
    },
];
